import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat, SINGLE_PLAYER } from '../bridge/seats.js';
import { type Card, type Suit, SUITS, fullDeck, isJoker, jokerCard, sameCard, sortHand } from '../trick/cards.js';
import { seededRandom, shuffled } from '../trick/rng.js';

export type CrazyEightsSeat = Seat;

export class IllegalCrazyEightsMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalCrazyEightsMoveError';
  }
}

export type CrazyEightsPhase = 'play' | 'done';

/**
 * Crazy Eights, the kids' favourite. Match the suit or the rank of the card on top of the discard pile, or play an
 * EIGHT (wild) and name any suit. With the Jokers option the red and the black Joker are wild too, just like an eight.
 * If you cannot play, draw one card: play it if you can, otherwise your turn ends.
 * The first player to empty their hand wins. No stakes: points are only a score.
 *
 * The state holds every hand and the stock; clients and robots read only `crazyEightsViewFor` (view.ts).
 */
export interface CrazyEightsGame {
  readonly seed: number;
  readonly dealer: Seat;
  /** The seats in play, clockwise (2 to 4 of the four table seats). */
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  /** Whether the deck holds the red and the black Joker (54 cards). Off by default, so a plain game is exactly as it always was. */
  readonly jokers: boolean;
  readonly hands: Readonly<Partial<Record<Seat, readonly Card[]>>>;
  /** Face-down cards, next card last. */
  readonly stock: readonly Card[];
  /** The pile; the card on top is the last. */
  readonly discard: readonly Card[];
  /** The suit a wild card (an eight or a Joker) was declared as, or null while the top card's own suit counts. */
  readonly declaredSuit: Suit | null;
  readonly phase: CrazyEightsPhase;
  readonly turn: Seat;
  /** True once the seat to play has drawn this turn (it may not draw again). */
  readonly drew: boolean;
  /** How many seats in a row have passed with nothing to play or draw; a full circle ends the hand. */
  readonly stuck: number;
  readonly winner: Seat | null;
}

export interface NewCrazyEightsOptions {
  readonly seed: number;
  /** 2 to 4 seats, in table order. Defaults to all four. */
  readonly seats?: readonly Seat[];
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
  /** Add the red and the black Joker to the deck: wild cards, like the eights. */
  readonly jokers?: boolean;
}

/** Five cards each, or seven when two play. */
export const handSizeFor = (players: number): number => (players === 2 ? 7 : 5);

const nextSeat = (seats: readonly Seat[], seat: Seat): Seat => seats[(seats.indexOf(seat) + 1) % seats.length] as Seat;

export function newCrazyEightsGame(opts: NewCrazyEightsOptions): CrazyEightsGame {
  const seats = (opts.seats ?? BRIDGE_SEATS).filter((s, i, a) => a.indexOf(s) === i);
  if (seats.length < 2 || seats.length > 4) throw new IllegalCrazyEightsMoveError(`Crazy Eights is for 2 to 4 players, not ${seats.length}`);
  const dealer = opts.dealer && seats.includes(opts.dealer) ? opts.dealer : (seats[0] as Seat);
  const jokers = opts.jokers === true;
  let deck = shuffled(jokers ? [...fullDeck(), jokerCard('red'), jokerCard('black')] : fullDeck(), seededRandom(opts.seed));
  const hands: Record<string, Card[]> = Object.fromEntries(seats.map((s) => [s, [] as Card[]]));
  const per = handSizeFor(seats.length);
  // One card at a time, starting with the seat after the dealer.
  const order = [...seats.slice(seats.indexOf(dealer) + 1), ...seats.slice(0, seats.indexOf(dealer) + 1)];
  for (let r = 0; r < per; r++) for (const s of order) (hands[s] as Card[]).push(deck.pop() as Card);
  // The first discard may not be wild (an eight or a Joker): tuck it under the stock and turn up the next card.
  let first = deck.pop() as Card;
  while (isWild(first)) {
    deck = [first, ...deck];
    first = deck.pop() as Card;
  }
  return {
    seed: opts.seed,
    dealer,
    seats,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    jokers,
    hands: Object.fromEntries(seats.map((s) => [s, sortHand(hands[s] as Card[])])),
    stock: deck,
    discard: [first],
    declaredSuit: null,
    phase: 'play',
    turn: nextSeat(seats, dealer),
    drew: false,
    stuck: 0,
    winner: null,
  };
}

/** An eight, or a Joker: playable on anything, and the player names the suit that follows. */
export const isWild = (card: Card): boolean => card.rank === 8 || isJoker(card);

export const topCard = (game: CrazyEightsGame): Card => game.discard[game.discard.length - 1] as Card;

/** The suit a play must match: the declared suit after a wild card, else the top card's suit. */
export const activeSuit = (game: CrazyEightsGame): Suit => game.declaredSuit ?? topCard(game).suit;

const handOf = (game: CrazyEightsGame, seat: Seat): readonly Card[] => game.hands[seat] ?? [];

/** The cards the seat to play may play now: a wild card, a card of the active suit, or a card of the top card's rank. */
export function crazyEightsLegalPlays(game: CrazyEightsGame): Card[] {
  if (game.phase !== 'play') return [];
  const top = topCard(game);
  const suit = activeSuit(game);
  // A Joker is a card of rank 15 with the suit of its colour; it must never count as a suit or rank match, only as wild.
  return handOf(game, game.turn).filter((c) => isWild(c) || (!isJoker(c) && (c.suit === suit || c.rank === top.rank)));
}

/** Cards that can still be drawn: the stock, plus the discard pile under its top card (it is reshuffled when the stock runs out). */
const drawable = (game: CrazyEightsGame): number => game.stock.length + Math.max(0, game.discard.length - 1);

export const canDraw = (game: CrazyEightsGame): boolean =>
  game.phase === 'play' && !game.drew && crazyEightsLegalPlays(game).length === 0 && drawable(game) > 0;

/** Move on to the next seat; a seat that cannot play or draw passes at once, and a full circle of passes ends the hand. */
function settle(game: CrazyEightsGame): CrazyEightsGame {
  let g = game;
  while (g.phase === 'play' && crazyEightsLegalPlays(g).length === 0 && !canDraw(g) && (g.drew || drawable(g) === 0)) {
    const stuck = g.stuck + 1;
    if (stuck >= g.seats.length) return endStuck({ ...g, stuck });
    g = { ...g, turn: nextSeat(g.seats, g.turn), drew: false, stuck };
  }
  return g;
}

const handValue = (cards: readonly Card[]): number => cards.reduce((a, c) => a + cardPoints(c), 0);

/** A blocked table: the seat with the lowest hand value wins (the first of them in table order on a tie). */
function endStuck(game: CrazyEightsGame): CrazyEightsGame {
  const best = game.seats.reduce((a, b) => (handValue(handOf(game, b)) < handValue(handOf(game, a)) ? b : a));
  return { ...game, phase: 'done', winner: best };
}

/** Scoring: an eight or a Joker is 50, a face card 10, an ace 1, any other card its face value. */
export function cardPoints(card: Card): number {
  if (isWild(card)) return 50;
  if (card.rank >= 11 && card.rank <= 13) return 10;
  if (card.rank === 14) return 1;
  return card.rank;
}

/** Play a card. A wild card (an eight or a Joker) needs `suit` (the suit you name); any other card must not carry one. */
export function crazyEightsPlay(game: CrazyEightsGame, card: Card, suit?: Suit): CrazyEightsGame {
  if (game.phase !== 'play') throw new IllegalCrazyEightsMoveError('the hand is over');
  if (!crazyEightsLegalPlays(game).some((c) => sameCard(c, card))) throw new IllegalCrazyEightsMoveError(`${game.turn} cannot play that card now`);
  if (isWild(card) && (suit === undefined || !SUITS.includes(suit))) throw new IllegalCrazyEightsMoveError(isJoker(card) ? 'a Joker needs the suit you name' : 'an eight needs the suit you name');
  if (!isWild(card) && suit !== undefined) throw new IllegalCrazyEightsMoveError('only an eight or a Joker names a suit');
  const seat = game.turn;
  const hand = handOf(game, seat).filter((c) => !sameCard(c, card));
  const hands = { ...game.hands, [seat]: hand };
  const discard = [...game.discard, card];
  if (hand.length === 0) return { ...game, hands, discard, declaredSuit: null, phase: 'done', winner: seat, drew: false, stuck: 0 };
  return settle({ ...game, hands, discard, declaredSuit: isWild(card) ? (suit as Suit) : null, turn: nextSeat(game.seats, seat), drew: false, stuck: 0 });
}

/** Draw one card (only when nothing is playable). If the drawn card cannot be played the turn ends; otherwise you may play it or pass. */
export function crazyEightsDraw(game: CrazyEightsGame): CrazyEightsGame {
  if (!canDraw(game)) throw new IllegalCrazyEightsMoveError('you cannot draw now');
  let stock = [...game.stock];
  let discard = [...game.discard];
  if (stock.length === 0) {
    // Recycle the pile under its top card, shuffled from the game's seed so a seeded game replays exactly.
    const keep = discard[discard.length - 1] as Card;
    stock = shuffled(discard.slice(0, -1), seededRandom(game.seed + discard.length));
    discard = [keep];
  }
  const card = stock.pop() as Card;
  const seat = game.turn;
  const hands = { ...game.hands, [seat]: sortHand([...handOf(game, seat), card]) };
  const next = { ...game, hands, stock, discard, drew: true, stuck: 0 };
  return settle(next);
}

/** Decline to play the card you just drew. */
export function crazyEightsPass(game: CrazyEightsGame): CrazyEightsGame {
  if (game.phase !== 'play' || !game.drew) throw new IllegalCrazyEightsMoveError('you can only pass after drawing');
  return settle({ ...game, turn: nextSeat(game.seats, game.turn), drew: false });
}

export interface CrazyEightsResult {
  readonly winner: Seat;
  /** The winner's score for the hand: the other hands' card points (a blocked table: the others' minus the winner's own). */
  readonly points: number;
  /** How many cards each seat still holds. */
  readonly cardsLeft: Readonly<Partial<Record<Seat, number>>>;
  readonly blocked: boolean;
}

export function crazyEightsResult(game: CrazyEightsGame): CrazyEightsResult | null {
  if (game.phase !== 'done' || !game.winner) return null;
  const winner = game.winner;
  const others = game.seats.filter((s) => s !== winner).reduce((a, s) => a + handValue(handOf(game, s)), 0);
  const blocked = handOf(game, winner).length > 0;
  return {
    winner,
    points: Math.max(0, others - (blocked ? handValue(handOf(game, winner)) : 0)),
    cardsLeft: Object.fromEntries(game.seats.map((s) => [s, handOf(game, s).length])),
    blocked,
  };
}

/** Scores add up over hands; the first to reach the target wins the game. */
export const CRAZY_EIGHTS_TARGET = 100;
export type CrazyEightsTotals = Readonly<Partial<Record<Seat, number>>>;

export function addHand(totals: CrazyEightsTotals, result: CrazyEightsResult): CrazyEightsTotals {
  return { ...totals, [result.winner]: (totals[result.winner] ?? 0) + result.points };
}

/** The seat that has reached the target with the highest total, or null while the game goes on. */
export function crazyEightsGameWinner(totals: CrazyEightsTotals, target = CRAZY_EIGHTS_TARGET): Seat | null {
  const entries = Object.entries(totals).filter((e): e is [Seat, number] => typeof e[1] === 'number');
  const best = Math.max(0, ...entries.map(([, v]) => v));
  if (best < target) return null;
  const leaders = entries.filter(([, v]) => v === best);
  return leaders.length === 1 ? (leaders[0] as [Seat, number])[0] : null; // a tie at the top plays another hand
}
